import { Form } from "antd";
import TextArea from "antd/es/input/TextArea";
import dayjs from "dayjs";
import { FC, useEffect, useMemo } from "react";
import { getNetworkInfoThunk } from "tm-shared/api";
import { Market } from "tm-shared/types";

import FlexContainer from "@/components/FlexContainer";
import { CONTRACT_TARGET_TYPES_OPTIONS } from "@/constants";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import {
    StyledDatePicker,
    StyledInput,
    StyledSelect,
} from "@/pages/HR/AddUser/Styled";
import { ContractFormValues } from "@/types/form";

interface ContractFormFieldsProps {
    editMode: boolean;
    market: Market;
    marketLoading: boolean;
}

export const ContractFormFields: FC<ContractFormFieldsProps> = ({
    editMode,
    market,
    marketLoading,
}) => {
    const dispatch = useAppDispatch();

    const { regions, networks } = useAppSelector((s) => s.user);
    const { contractTypesList } = useAppSelector((s) => s.offline);

    // --- 1. Fetched network info if missing
    useEffect(() => {
        if (market?.networkId && !networks?.[market.networkId]) {
            dispatch(getNetworkInfoThunk({ network: market.networkId }));
        }
    }, [market?.networkId]);

    // --- 2. Vocabulary for filtering + correct labels
    const targetTypeVocabulary = useMemo(
        () => ({
            market: market?.id,
            network: market?.networkId,
            contragent: market?.contragent?.id,
            storage: market?.storage?.id,
            mainRegion: market?.address?.mainRegionId,
        }),
        [market],
    );

    // --- 3. Target type options with human-readable suffixes
    const availableTargetTypeOptions = useMemo(() => {
        const labelMap: Record<string, string> = {
            contragent: market?.contragent?.name || "Невідома юр.особа",
            mainRegion:
                regions?.[market?.address?.mainRegionId || ""] ||
                "Невідомий регіон",
            storage: market?.storage?.name || "Невідомий склад",
            network: networks?.[market?.networkId || ""] || "Невідома мережа",
        };

        return CONTRACT_TARGET_TYPES_OPTIONS.filter(
            (opt) => targetTypeVocabulary[opt.value],
        ).map((opt) => ({
            value: opt.value,
            label: labelMap[opt.value]
                ? `${opt.label} (${labelMap[opt.value]})`
                : opt.label,
        }));
    }, [targetTypeVocabulary, regions, networks, market]);

    // --- 4. Select options for contract types
    const contractTypeSelectOptions = useMemo(
        () =>
            Object.entries(contractTypesList).map(([value, label]) => ({
                value,
                label,
            })),
        [contractTypesList],
    );

    // --- 5. Shared date props
    const dateValueProps = (v?: string) => ({
        value: v ? dayjs(v) : null,
    });

    const dateFromEvent = (d?: dayjs.Dayjs | null) =>
        d ? d.format("YYYY-MM-DD") : undefined;

    return (
        <>
            <Form.Item<ContractFormValues>
                name="type"
                label="Тип контракту:"
                rules={[{ required: true, message: "Оберіть тип контракту" }]}
            >
                <StyledSelect
                    options={contractTypeSelectOptions}
                    placeholder="Оберіть тип"
                    disabled={editMode}
                />
            </Form.Item>

            <Form.Item<ContractFormValues>
                name="targetType"
                label="Рівень дії:"
                rules={[{ required: true, message: "Оберіть рівень дії" }]}
            >
                <StyledSelect
                    options={availableTargetTypeOptions}
                    placeholder="Оберіть рівень дії"
                    disabled={editMode}
                    loading={marketLoading}
                />
            </Form.Item>

            <Form.Item<ContractFormValues>
                name="name"
                label="Назва контракту"
                rules={[{ required: true, message: "Введіть назву контракту" }]}
            >
                <StyledInput placeholder="Введіть назву контракту" />
            </Form.Item>

            <Form.Item<ContractFormValues>
                name="description"
                label="Опис"
                style={{ marginBottom: 16 }}
            >
                <TextArea placeholder="Поле для введення" />
            </Form.Item>

            <FlexContainer gap="16px">
                <Form.Item<ContractFormValues>
                    name="startDate"
                    label="Дата початку:"
                    rules={[
                        { required: true, message: "Оберіть дату початку" },
                    ]}
                    getValueProps={dateValueProps}
                    getValueFromEvent={dateFromEvent}
                    style={{ width: "100%" }}
                >
                    <StyledDatePicker placeholder="Обрати дату" />
                </Form.Item>

                <Form.Item<ContractFormValues>
                    name="endDate"
                    label="Дата завершення:"
                    getValueProps={dateValueProps}
                    getValueFromEvent={dateFromEvent}
                    style={{ width: "100%" }}
                >
                    <StyledDatePicker placeholder="Обрати дату" />
                </Form.Item>
            </FlexContainer>
        </>
    );
};
